import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

// Peaks are extracted from the supplied MP3s; the playhead follows the audio clock.
export const VoiceWaveform = ({ voice, player, active }) => {
  const canvasRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const playing = active && player.status === "playing";

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    let frame;
    const samples = new Uint8Array(256);
    const draw = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const ratio = window.devicePixelRatio || 1;
      if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
        canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio);
      }
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);
      const elapsed = active ? (player.audioRef.current?.currentTime || player.time) : 0;
      const fraction = Math.min(1, elapsed / (player.duration || voice.duration));
      let amplitude = 0;
      if (playing && !reducedMotion && player.analyserRef.current) {
        player.analyserRef.current.getByteTimeDomainData(samples);
        amplitude = Math.sqrt(samples.reduce((sum, v) => sum + ((v - 128) / 128) ** 2, 0) / samples.length);
      }
      const step = width / voice.peaks.length;
      voice.peaks.forEach((peak, i) => {
        const nearHead = Math.abs(i / voice.peaks.length - fraction) < 0.06;
        const h = Math.max(2, peak * (height - 18) * (nearHead ? 1 + amplitude * 0.7 : 1));
        context.fillStyle = elapsed > 0 && i / voice.peaks.length <= fraction ? voice.accent : voice.muted;
        context.fillRect(i * step, (height - h) / 2, Math.max(1, step * 0.48), h);
      });
      if (elapsed > 0) {
        context.fillStyle = voice.accent;
        context.fillRect(Math.min(width - 2, fraction * width), 2, 2, height - 4);
      }
      if (playing && !reducedMotion) frame = requestAnimationFrame(draw);
    };
    draw();
    const resize = new ResizeObserver(() => { cancelAnimationFrame(frame); draw(); });
    resize.observe(canvas);
    return () => { cancelAnimationFrame(frame); resize.disconnect(); };
  }, [active, playing, reducedMotion, player.audioRef, player.analyserRef, player.time, player.duration, voice]);

  return (
    <div className="signal-waveform" data-testid={`signal-waveform-${voice.id}`}>
      <canvas ref={canvasRef} aria-hidden="true" />
      <input type="range" min="0" max={active ? player.duration || voice.duration : voice.duration} step="0.01"
        value={active ? player.time : 0} onChange={(e) => player.seek(Number(e.target.value))}
        disabled={!active || !player.duration} aria-label={`Seek ${voice.name} sample`}
        aria-valuetext={`${Math.floor(active ? player.time : 0)} seconds`}
        data-testid={`signal-voice-seek-${voice.id}`} />
    </div>
  );
};